/**
 * Layers — named bands of a screen's top-level children.
 *
 * See docs/DESIGN_BUILDER.md §4 Layers. The rules this file enforces:
 *
 * - a layer owns no nodes; each child of the screen carries `layerId`;
 * - a layer's nodes are **contiguous** in the children array, and the layers
 *   appear in the array in the same order they are listed, so the array alone
 *   still decides paint order and no renderer has to know layers exist;
 * - a design with no layers yet gets them derived from what is already drawn,
 *   without changing how it looks.
 */

import type { DesignLayer, DesignLayerKind, DesignNode } from '../design-builder.types';
import { roundGeometry } from './design-geometry.utils';

/** Back to front — a layer's default position among its siblings. */
export const LAYER_KIND_ORDER: readonly DesignLayerKind[] = [
  'BACKGROUND',
  'SHAPES',
  'COMPONENT',
  'CONTROLS',
  'TEXT',
  'OVERLAY',
  // Last, so it heads the panel. Groups draw nothing, so where they sit in the
  // paint order is irrelevant — what matters is that they are easy to reach.
  'GROUP',
];

export const LAYER_KIND_LABELS: Record<DesignLayerKind, string> = {
  BACKGROUND: 'Background',
  SHAPES: 'Shapes',
  COMPONENT: 'Components',
  CONTROLS: 'Controls',
  TEXT: 'Text',
  OVERLAY: 'Overlay',
  GROUP: 'Groups',
};

export const LAYER_KIND_ICONS: Record<DesignLayerKind, string> = {
  BACKGROUND: 'wallpaper',
  SHAPES: 'category',
  COMPONENT: 'insert_chart',
  CONTROLS: 'tune',
  TEXT: 'title',
  OVERLAY: 'flip_to_front',
  GROUP: 'folder',
};

/** Widgets that are a form control rather than a chart — the CONTROLS band. */
const CONTROL_LAYOUT_TYPES: ReadonlySet<string> = new Set([
  'DROPDOWN', 'SLIDER', 'ZONE_SLIDER', 'INPUT_BOX', 'FILE_UPLOAD', 'POPUP',
]);

/** Which band a node belongs to when nothing says otherwise. */
export function layerKindOf(node: DesignNode): DesignLayerKind {
  if (node.type === 'GROUP') return 'GROUP';
  if (node.type === 'TEXT') return 'TEXT';
  if (node.type === 'WIDGET') {
    return CONTROL_LAYOUT_TYPES.has(node.widget?.layoutType ?? '') ? 'CONTROLS' : 'COMPONENT';
  }
  return 'SHAPES';
}

/** A screen has at most one band of each kind, so this is its whole identity. */
export function layerKinds(frame: DesignNode): ReadonlySet<DesignLayerKind> {
  return new Set((frame.layers ?? []).map((layer) => layer.kind));
}

let layerSequence = 0;

export function createLayer(kind: DesignLayerKind, name?: string): DesignLayer {
  layerSequence += 1;
  return {
    id: `lyr_${Date.now().toString(36)}${layerSequence.toString(36)}`,
    name: name ?? LAYER_KIND_LABELS[kind],
    kind,
  };
}

export function isLayerKind(value: unknown): value is DesignLayerKind {
  return typeof value === 'string' && (LAYER_KIND_ORDER as readonly string[]).includes(value);
}

/**
 * Give a screen frame a valid set of layers and put its children in layer
 * order. Runs on every commit, so it is the one place the invariants hold.
 */
export function syncScreenLayers(frame: DesignNode): void {
  const children = frame.children ?? [];
  const layers = (frame.layers ?? []).filter((layer) => !!layer?.id && isLayerKind(layer.kind));
  liftShapeChildren(children);
  syncGroups(children);

  if (!children.length) {
    frame.layers = layers;
    return;
  }

  frame.layers = layers.length
    ? assignToLayers(children, mergeDuplicateKinds(layers, children))
    : deriveLayers(children);
  orderChildrenByLayer(frame);
  // Only a direct child of the screen sits in a layer; anything deeper is
  // carried by its ancestor, so a stale id from a reparent is cleared.
  for (const child of children) clearNestedLayerIds(child.children);
}

/** Children sorted into layer order, each layer's nodes kept in their own order. */
function orderChildrenByLayer(frame: DesignNode): void {
  const order = new Map((frame.layers ?? []).map((layer, index) => [layer.id, index]));
  const children = frame.children ?? [];
  const positioned = children.map((node, index) => ({
    node,
    layer: order.get(node.layerId ?? '') ?? order.size,
    index,
  }));
  positioned.sort((a, b) => a.layer - b.layer || a.index - b.index);
  frame.children = positioned.map((entry) => entry.node);
}

/**
 * Only a screen owns children now (see `acceptsChildren`), so anything still
 * nested — a panel rectangle that swallowed half a screen, or a group from
 * before groups became references — is lifted out to sit beside its parent.
 *
 * Lifted in place, right after the parent, so it keeps painting above it, and
 * rebased out of the parent's coordinate space so nothing moves on screen. A
 * GROUP keeps hold of what it had, as `memberIds`.
 */
function liftShapeChildren(nodes: DesignNode[]): void {
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    if (node.type === 'FRAME') {
      if (node.children?.length) liftShapeChildren(node.children);
      continue;
    }
    if (!node.children?.length) continue;

    const lifted = node.children;
    delete node.children;
    for (const child of lifted) {
      child.x += node.x;
      child.y += node.y;
    }
    if (node.type === 'GROUP') node.memberIds = lifted.map((child) => child.id);
    nodes.splice(index + 1, 0, ...lifted);
    // The loop walks into them next, so a chain of nested parents unwinds too.
  }
}

/**
 * Keep every group honest: references that no longer resolve are dropped, a
 * group that has lost all of them goes with them, and the rest are sized to
 * what they hold — which is why a group cannot be moved or resized out of step
 * with its members.
 */
/**
 * Page level (outside any screen) gets the same invariants as a screen's
 * children: shapes or old-style groups holding children are lifted out — a
 * group keeps them as `memberIds` — then every group is sized to its members.
 */
export function syncPageGroups(nodes: DesignNode[]): void {
  liftShapeChildren(nodes);
  syncGroups(nodes);
}

function syncGroups(children: DesignNode[]): void {
  const byId = new Map(children.map((node) => [node.id, node]));

  for (const node of children) {
    if (!node.memberIds) continue;
    node.memberIds = node.memberIds.filter((id) => id !== node.id && byId.has(id));
  }
  for (let index = children.length - 1; index >= 0; index -= 1) {
    const node = children[index];
    if (node.type === 'GROUP' && !node.memberIds?.length) {
      children.splice(index, 1);
      byId.delete(node.id);
    }
  }

  for (const node of children) {
    if (!node.memberIds?.length) continue;
    const boxes = memberLeavesOf(node, byId);
    if (!boxes.length) continue;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const member of boxes) {
      minX = Math.min(minX, member.x);
      minY = Math.min(minY, member.y);
      maxX = Math.max(maxX, member.x + member.width);
      maxY = Math.max(maxY, member.y + member.height);
    }
    node.x = roundGeometry(minX);
    node.y = roundGeometry(minY);
    node.width = Math.max(1, roundGeometry(maxX - minX));
    node.height = Math.max(1, roundGeometry(maxY - minY));
  }
}

/**
 * The real nodes a group ends at, following nested groups down. Groups hold
 * references, so a group of groups must resolve to the things that actually
 * have a box before anything is measured or moved.
 */
export function memberLeavesOf(
  group: DesignNode,
  byId: ReadonlyMap<string, DesignNode>,
  seen: Set<string> = new Set([group.id]),
): DesignNode[] {
  const leaves: DesignNode[] = [];
  for (const id of group.memberIds ?? []) {
    if (seen.has(id)) continue;
    seen.add(id);
    const member = byId.get(id);
    if (!member) continue;
    if (member.memberIds?.length) leaves.push(...memberLeavesOf(member, byId, seen));
    else leaves.push(member);
  }
  return leaves;
}

function clearNestedLayerIds(nodes: DesignNode[] | undefined): void {
  for (const node of nodes ?? []) {
    if (node.layerId) delete node.layerId;
    clearNestedLayerIds(node.children);
  }
}

/** Point every child at a live layer, adding a band for a kind that has none. */
function assignToLayers(children: DesignNode[], layers: DesignLayer[]): DesignLayer[] {
  const next = [...layers];
  const byId = new Set(next.map((layer) => layer.id));

  for (const child of children) {
    if (child.layerId && byId.has(child.layerId)) continue;
    const kind = layerKindOf(child);
    let layer = next.find((candidate) => candidate.kind === kind);
    if (!layer) {
      layer = createLayer(kind);
      next.splice(defaultIndexFor(next, kind), 0, layer);
      byId.add(layer.id);
    }
    child.layerId = layer.id;
  }
  return next;
}

/** Where a new band of this kind belongs among the ones already there. */
export function defaultIndexFor(layers: readonly DesignLayer[], kind: DesignLayerKind): number {
  const rank = LAYER_KIND_ORDER.indexOf(kind);
  const index = layers.findIndex((layer) => LAYER_KIND_ORDER.indexOf(layer.kind) > rank);
  return index === -1 ? layers.length : index;
}

/**
 * Layers for a screen that has none: **exactly one band per kind**, so every
 * text is in Text and every chart in Components, however the design was drawn.
 *
 * A design where a shape was drawn on top of a chart therefore restacks — that
 * shape joins the Shapes band and lands behind it. The fix is to drag the band
 * above Components in the panel, which moves all of them together.
 */
export function deriveLayers(children: DesignNode[]): DesignLayer[] {
  const kinds = children.map(layerKindOf);
  const used = LAYER_KIND_ORDER.filter((kind) => kinds.includes(kind));
  const layers = used.map((kind) => createLayer(kind));
  children.forEach((child, index) => {
    child.layerId = layers[used.indexOf(kinds[index])].id;
  });
  return layers;
}

/**
 * Fold any second band of a kind into the first — designs saved while bands
 * could repeat ("Text", "Text 2") come back as one Text.
 */
function mergeDuplicateKinds(layers: DesignLayer[], children: DesignNode[]): DesignLayer[] {
  const keep = new Map<DesignLayerKind, DesignLayer>();
  const remap = new Map<string, string>();

  for (const layer of layers) {
    const first = keep.get(layer.kind);
    if (first) remap.set(layer.id, first.id);
    else keep.set(layer.kind, layer);
  }
  if (!remap.size) return layers;

  for (const child of children) {
    const target = remap.get(child.layerId ?? '');
    if (target) child.layerId = target;
  }
  return layers.filter((layer) => !remap.has(layer.id));
}

/**
 * The outermost group holding `id`, searching `children` (a screen's nodes).
 *
 * A group draws nothing, so a click on the canvas always lands on a MEMBER.
 * Resolving it to the group is what makes a group behave like one thing.
 */
export function outermostGroupOf(children: readonly DesignNode[], id: string): DesignNode | null {
  const groups = children.filter((child) => child.memberIds?.length);
  if (!groups.length) return null;

  const byId = new Map(children.map((child) => [child.id, child] as const));
  // Matched against every id below the group, not just the leaves, so a group
  // nested in another one resolves to the outer one as well.
  const holds = (group: DesignNode, target: string, seen = new Set([group.id])): boolean =>
    (group.memberIds ?? []).some((memberId) => {
      if (memberId === target) return true;
      if (seen.has(memberId)) return false;
      seen.add(memberId);
      const member = byId.get(memberId);
      return !!member?.memberIds?.length && holds(member, target, seen);
    });

  const holding = groups.filter((group) => holds(group, id));
  if (!holding.length) return null;

  // The one no other group references is the outermost of the nest.
  const nested = new Set(groups.flatMap((group) => group.memberIds ?? []));
  return holding.find((group) => !nested.has(group.id)) ?? holding[0];
}

/** The screen frame that owns `layerId`, with that layer. */
export function locateLayer(
  nodes: readonly DesignNode[],
  layerId: string,
): { frame: DesignNode; layer: DesignLayer; index: number } | null {
  for (const node of nodes) {
    const index = node.layers?.findIndex((layer) => layer.id === layerId) ?? -1;
    if (index >= 0) return { frame: node, layer: node.layers![index], index };
  }
  return null;
}

/** The nodes painted in each layer of a screen, in paint order. */
export function nodesByLayer(frame: DesignNode): Map<string, DesignNode[]> {
  const grouped = new Map<string, DesignNode[]>();
  for (const layer of frame.layers ?? []) grouped.set(layer.id, []);
  for (const child of frame.children ?? []) {
    const bucket = grouped.get(child.layerId ?? '');
    if (bucket) bucket.push(child);
  }
  return grouped;
}
